/**
 * Speurtocht Management Screen
 * List all speurtochts and allow creating new ones
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Alert,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import {
  Speurtocht,
  SpeurttochtStatus,
  STEP_TYPE_COLORS,
  useSpeurttochtAdmin,
} from "@/modules/speurtocht";

/**
 * Extended Speurtocht with step count
 */
interface SpeurttochtWithCount extends Speurtocht {
  steps_count: number;
  sessions_count: number;
}

export default function SpeurttochtListScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { activeWeekend } = useAuth();
  const { updateStatus } = useSpeurttochtAdmin();

  // State
  const [speurtochts, setSpeurtochts] = useState<SpeurttochtWithCount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Fetch speurtochts
   */
  const fetchSpeurtochts = useCallback(async () => {
    if (!activeWeekend?.id) {
      setSpeurtochts([]);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("speurtochts")
        .select(`
          *,
          steps_count:speurtocht_steps(count),
          sessions_count:speurtocht_sessions(count)
        `)
        .eq("weekend_id", activeWeekend.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching speurtochts:", error);
        return;
      }

      // Transform the count
      const speurtochtsWithCount = (data || []).map((s: any) => ({
        ...s,
        steps_count: s.steps_count?.[0]?.count || 0,
        sessions_count: s.sessions_count?.[0]?.count || 0,
      }));

      setSpeurtochts(speurtochtsWithCount);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeWeekend?.id]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchSpeurtochts();
    setIsRefreshing(false);
  }, [fetchSpeurtochts]);

  /**
   * Update status
   */
  /**
   * Update speurtocht status using the admin hook (sends notifications)
   */
  const handleUpdateStatus = async (
    speurtocht: SpeurttochtWithCount,
    newStatus: SpeurttochtStatus
  ) => {
    const success = await updateStatus(speurtocht.id, newStatus);

    if (!success) {
      Alert.alert("Fout", "Kon status niet bijwerken");
      return;
    }

    setSpeurtochts((prev) =>
      prev.map((s) =>
        s.id === speurtocht.id ? { ...s, status: newStatus } : s
      )
    );
  };

  /**
   * Toggle status between draft and active
   */
  const handleToggleActive = (speurtocht: SpeurttochtWithCount) => {
    if (speurtocht.steps_count === 0) {
      Alert.alert(
        "Geen stappen",
        "Voeg eerst stappen toe voordat je de speurtocht activeert"
      );
      return;
    }

    const newStatus = speurtocht.status === "active" ? "paused" : "active";
    handleUpdateStatus(speurtocht, newStatus);
  };

  /**
   * Delete speurtocht
   */
  const handleDelete = (speurtocht: SpeurttochtWithCount) => {
    if (speurtocht.sessions_count > 0) {
      Alert.alert(
        "Niet mogelijk",
        "Deze speurtocht heeft al sessies. Je kunt hem archiveren in plaats van verwijderen.",
        [
          { text: "Annuleren", style: "cancel" },
          {
            text: "Archiveren",
            onPress: () => handleUpdateStatus(speurtocht, "archived"),
          },
        ]
      );
      return;
    }

    Alert.alert(
      "Speurtocht Verwijderen",
      `Weet je zeker dat je "${speurtocht.name}" wilt verwijderen?\n\nAlle stappen worden ook verwijderd.`,
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            const { error } = await supabase
              .from("speurtochts")
              .delete()
              .eq("id", speurtocht.id);

            if (error) {
              Alert.alert("Fout", "Kon speurtocht niet verwijderen");
              return;
            }

            setSpeurtochts((prev) => prev.filter((s) => s.id !== speurtocht.id));
          },
        },
      ]
    );
  };

  useEffect(() => {
    fetchSpeurtochts();
  }, [fetchSpeurtochts]);

  /**
   * Get status badge config
   */
  const getStatusBadge = (status: SpeurttochtStatus) => {
    const configs = {
      draft: { color: "#F59E0B", label: "Concept", icon: "create" },
      active: { color: "#10B981", label: "Actief", icon: "play" },
      paused: { color: "#6B7280", label: "Gepauzeerd", icon: "pause" },
      completed: { color: "#8B5CF6", label: "Afgerond", icon: "checkmark-circle" },
      archived: { color: "#6B7280", label: "Gearchiveerd", icon: "archive" },
    };
    return configs[status];
  };

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <View className="px-4 py-4 flex-row items-center justify-between">
        <View className="flex-row items-center">
          <TouchableOpacity
            onPress={() => router.back()}
            className="w-10 h-10 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: colors.surface }}
            accessibilityRole="button"
            accessibilityLabel="Terug"
          >
            <Ionicons name="arrow-back" size={22} color={colors.text} />
          </TouchableOpacity>
          <View>
            <Text className="text-xl font-bold" style={{ color: colors.text }}>
              Speurtochten
            </Text>
            <Text className="text-sm" style={{ color: colors.textSecondary }}>
              {speurtochts.length} speurtocht{speurtochts.length !== 1 ? "en" : ""}
            </Text>
          </View>
        </View>

        <TouchableOpacity
          onPress={() => router.push("/(admin)/games/speurtocht/create")}
          className="w-10 h-10 rounded-full items-center justify-center"
          style={{ backgroundColor: "#10B981" }}
          accessibilityRole="button"
          accessibilityLabel="Nieuwe speurtocht"
        >
          <Ionicons name="add" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {isLoading ? (
          <View className="py-12 items-center">
            <ActivityIndicator size="large" color="#10B981" />
          </View>
        ) : !activeWeekend ? (
          <View className="py-12 items-center px-4">
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: "#10B98120" }}
            >
              <Ionicons name="calendar-outline" size={40} color="#10B981" />
            </View>
            <Text className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              Geen actief weekend
            </Text>
            <Text className="text-center" style={{ color: colors.textSecondary }}>
              Activeer eerst een weekend om speurtochten te beheren
            </Text>
          </View>
        ) : speurtochts.length === 0 ? (
          <View className="py-12 items-center px-4">
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: "#10B98120" }}
            >
              <Ionicons name="map" size={40} color="#10B981" />
            </View>
            <Text className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              Geen speurtochten
            </Text>
            <Text className="text-center mb-6" style={{ color: colors.textSecondary }}>
              Maak je eerste speurtocht aan voor dit weekend
            </Text>
            <TouchableOpacity
              onPress={() => router.push("/(admin)/games/speurtocht/create")}
              className="px-6 py-3 rounded-xl flex-row items-center"
              style={{ backgroundColor: "#10B981" }}
            >
              <Ionicons name="add" size={20} color="#FFFFFF" />
              <Text className="ml-2 font-bold text-white">Nieuwe Speurtocht</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View className="px-4 pb-4">
            {speurtochts.map((speurtocht) => {
              const statusBadge = getStatusBadge(speurtocht.status);

              return (
                <TouchableOpacity
                  key={speurtocht.id}
                  onPress={() =>
                    router.push(`/(admin)/games/speurtocht/${speurtocht.id}/steps`)
                  }
                  activeOpacity={0.7}
                  className="rounded-xl p-4 mb-3"
                  style={{
                    backgroundColor:
                      speurtocht.status === "active"
                        ? `${speurtocht.color}15`
                        : colors.surface,
                    borderWidth: 2,
                    borderColor:
                      speurtocht.status === "active"
                        ? speurtocht.color
                        : colors.border,
                    opacity: speurtocht.status === "archived" ? 0.6 : 1,
                  }}
                >
                  <View className="flex-row items-start">
                    {/* Icon */}
                    <View
                      className="w-12 h-12 rounded-xl items-center justify-center mr-3"
                      style={{ backgroundColor: `${speurtocht.color}25` }}
                    >
                      <Ionicons
                        name={speurtocht.icon as any}
                        size={24}
                        color={speurtocht.color}
                      />
                    </View>

                    {/* Info */}
                    <View className="flex-1">
                      <Text className="font-bold text-base" style={{ color: colors.text }}>
                        {speurtocht.name}
                      </Text>
                      {speurtocht.description && (
                        <Text
                          className="text-sm mt-0.5"
                          style={{ color: colors.textSecondary }}
                          numberOfLines={1}
                        >
                          {speurtocht.description}
                        </Text>
                      )}

                      {/* Stats Row */}
                      <View className="flex-row items-center mt-2 flex-wrap">
                        <View
                          className="flex-row items-center px-2 py-1 rounded mr-2 mb-1"
                          style={{ backgroundColor: colors.surfaceSecondary }}
                        >
                          <Ionicons name="footsteps" size={12} color={colors.textSecondary} />
                          <Text className="text-xs ml-1" style={{ color: colors.textSecondary }}>
                            {speurtocht.steps_count} stappen
                          </Text>
                        </View>

                        <View
                          className="flex-row items-center px-2 py-1 rounded mr-2 mb-1"
                          style={{ backgroundColor: "#F59E0B20" }}
                        >
                          <Ionicons name="star" size={12} color="#F59E0B" />
                          <Text className="text-xs ml-1" style={{ color: "#F59E0B" }}>
                            {speurtocht.steps_count * speurtocht.points_per_step +
                              speurtocht.completion_bonus +
                              speurtocht.first_team_bonus}{" "}
                            max pts
                          </Text>
                        </View>

                        {speurtocht.has_time_limit && speurtocht.max_duration_minutes && (
                          <View
                            className="flex-row items-center px-2 py-1 rounded mb-1"
                            style={{ backgroundColor: colors.surfaceSecondary }}
                          >
                            <Ionicons name="time" size={12} color={colors.textSecondary} />
                            <Text className="text-xs ml-1" style={{ color: colors.textSecondary }}>
                              {speurtocht.max_duration_minutes} min
                            </Text>
                          </View>
                        )}
                      </View>

                      {/* Status Badge */}
                      <View
                        className="flex-row items-center mt-2 px-2 py-1 rounded-full self-start"
                        style={{ backgroundColor: `${statusBadge.color}20` }}
                      >
                        <Ionicons name={statusBadge.icon as any} size={12} color={statusBadge.color} />
                        <Text
                          className="text-xs ml-1 font-medium"
                          style={{ color: statusBadge.color }}
                        >
                          {statusBadge.label}
                        </Text>
                        {speurtocht.sessions_count > 0 && (
                          <Text
                            className="text-xs ml-2"
                            style={{ color: statusBadge.color }}
                          >
                            • {speurtocht.sessions_count} teams
                          </Text>
                        )}
                      </View>
                    </View>

                    {/* Actions */}
                    <View className="items-center">
                      {/* Progress */}
                      {speurtocht.sessions_count > 0 && (
                        <TouchableOpacity
                          onPress={() =>
                            router.push(
                              `/(admin)/games/speurtocht/${speurtocht.id}/progress`
                            )
                          }
                          className="w-8 h-8 rounded-full items-center justify-center mb-1"
                          style={{ backgroundColor: "#8B5CF620" }}
                        >
                          <Ionicons name="stats-chart" size={14} color="#8B5CF6" />
                        </TouchableOpacity>
                      )}

                      {/* Toggle Active */}
                      <TouchableOpacity
                        onPress={() => handleToggleActive(speurtocht)}
                        className="w-8 h-8 rounded-full items-center justify-center mb-1"
                        style={{
                          backgroundColor:
                            speurtocht.status === "active" ? "#10B98120" : colors.surfaceSecondary,
                        }}
                      >
                        <Ionicons
                          name={speurtocht.status === "active" ? "pause" : "play"}
                          size={14}
                          color={speurtocht.status === "active" ? "#10B981" : colors.textSecondary}
                        />
                      </TouchableOpacity>

                      {/* Edit */}
                      <TouchableOpacity
                        onPress={() =>
                          router.push(`/(admin)/games/speurtocht/${speurtocht.id}`)
                        }
                        className="w-8 h-8 rounded-full items-center justify-center mb-1"
                        style={{ backgroundColor: colors.surfaceSecondary }}
                      >
                        <Ionicons name="pencil" size={14} color={colors.textSecondary} />
                      </TouchableOpacity>

                      {/* Delete */}
                      <TouchableOpacity
                        onPress={() => handleDelete(speurtocht)}
                        className="w-8 h-8 rounded-full items-center justify-center"
                        style={{ backgroundColor: colors.error + "20" }}
                      >
                        <Ionicons name="trash" size={14} color={colors.error} />
                      </TouchableOpacity>
                    </View>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        )}

        <View className="h-24" />
      </ScrollView>

      {/* Floating Add Button */}
      {speurtochts.length > 0 && (
        <TouchableOpacity
          onPress={() => router.push("/(admin)/games/speurtocht/create")}
          className="absolute bottom-6 right-6 w-14 h-14 rounded-full items-center justify-center shadow-lg"
          style={{
            backgroundColor: "#10B981",
            shadowColor: "#10B981",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 8,
          }}
          accessibilityRole="button"
          accessibilityLabel="Nieuwe speurtocht toevoegen"
        >
          <Ionicons name="add" size={28} color="#FFFFFF" />
        </TouchableOpacity>
      )}
    </SafeAreaView>
  );
}

